import BaseClass from "../../../util/BaseClass";
import ClosestFacility from "../../../aiim/base/ClosestFacility";
import Context from "../../../context/Context";
import FieldNames from "../../../aiim/datasets/FieldNames";
import Topic from "../../../context/Topic";
import * as aiimUtil from "../../../aiim/util/aiimUtil";
import * as dateUtil from "../Events/dateUtil";
import moment from "moment";
import {
  dropUnitPins,
  setReservationsDefExp
} from "../More/Actions/BookWorkspace/WorkspaceReservation/reservationsLayerUtil";
import { goToFeature } from "../../../util/mapUtil";
import { isNetworkError } from "../../../util/networkUtil";

export default class MeetingRoomsModel extends BaseClass {

  parentComponent;

  constructor(props) {
    super(props);
    this.getCriteria();
  }

  canSortByBluedotDistance() {
    const url = Context.instance.config.closestFacilityServiceUrl;
    if (url) {
      const graphic = Context.instance.bluedot.graphic;
      return !!graphic;
    }
    return false;
  }

  canSortByCapacity() {
    const ds = Context.instance.aiim.datasets.units;
    if (ds && ds.layer2D) {
      const fld = aiimUtil.findField(ds.layer2D.fields,FieldNames.UNITS_CAPACITY);
      return !!fld;
    }
    return false;
  }

  canSortByHomeDistance() {
    const url = Context.instance.config.closestFacilityServiceUrl;
    if (url) {
      const referenceLayer = Context.instance.session.referenceLayer;
      const item = referenceLayer.homeLocation;
      if (item && item.isValid()) {
        return true;
      }
    }
    return false;
  }

  executeTask(type) {
    const task = {
      criteria: this.getCriteria(),
      criteriaString: null,
      items: this.parentComponent.state.items,
      filteredItems: this.parentComponent.state.filteredItems,
      reservedUnits: this.parentComponent.state.reservedUnits
    }
    const stateData = {};
    this.parentComponent.setState({working: true});
    Promise.resolve().then(() => {
      if (type === "sortUnits") {
        return this.sortUnits(task).then(() => {
          stateData.filteredItems = task.filteredItems;
        })
      } else if (type === "filterUnits") {
        return this.filterUnits(task).then(() => {
          stateData.filteredItems = task.filteredItems;
        })
      } else if (type === "queryReservations") {
        return this.queryReservations(task).then(() => {
          stateData.criteriaString = task.criteriaString;
          stateData.filteredItems = task.filteredItems;
          stateData.reservedUnits = task.reservedUnits;
        })
      }
    }).then(() => {
      if (!this.parentComponent._mounted) return;
      stateData.working = false
      this.parentComponent.setState(stateData);

      // Drop pins, filter layer, and zoom to first feature
      this.onRoomsListed(task);
    }).catch(ex => {
      console.error(ex);
      if (!this.parentComponent._mounted) return;
      this.parentComponent.setState({working: false});
      Topic.publishErrorAccessingData();
    })
  }

  filterUnits(task) {
    const items = task.items;
    const criteria = task.criteria;
    const reservedUnits = task.reservedUnits;
    const reservedUnitIndex = (reservedUnits && reservedUnits.index) || {};
    const capacity = criteria.filter.capacity;
    const facilityId = criteria.filter.facilityId;
    const levelId = criteria.filter.levelId;
    const siteId = criteria.filter.siteId;
    const hasCapacity = (typeof capacity === "number");
    const hasFaciliyId = !!facilityId;
    const hasLevelId = !!levelId;
    const hasSiteId = !!siteId;

    let filteredItems = null;
    if (items && items.length > 0) {
      filteredItems = [];
      items.forEach(item => {
        let ok = true;
        if (ok) {
          ok = !reservedUnitIndex[item.unitId];
        }
        if (ok && hasLevelId) {
          ok = (item.levelId === levelId);
        }
        if (ok && hasFaciliyId) {
          ok = (item.facilityId === facilityId);
        }
        if (ok && hasSiteId) {
          ok = (item.siteId === siteId);
        }
        if (ok && hasCapacity) {
          ok = (item.capacity >= capacity);
        }
        if (ok) filteredItems.push(item);
      });
    }
    task.filteredItems = filteredItems;

    return this.sortUnits(task);
  }

  onRoomsListed(task) {
    const { filteredItems, criteria } = task;
    if (!filteredItems || !filteredItems.length) {
      this.onNoRoomsListed(task);
      return;
    }
    const unitFeatures = filteredItems.map((item) => item.feature);

    const units = Context.getInstance().aiim.datasets.units;
    const unitsLayer = units && units.layer2D;
    const floorField = unitsLayer && unitsLayer.floorInfo && unitsLayer.floorInfo.floorField;
    const levels = Context.getInstance().aiim.datasets.levels;

    // Make sure the unit has accessible level data
    const validateLevelData = (unit) => {
      const attributes = unit.attributes;
      const levelId = floorField
        ? aiimUtil.getAttributeValue(attributes, floorField)
        : aiimUtil.getAttributeValue(attributes, FieldNames.LEVEL_ID);

      return levels.getLevelData(levelId);
    }

    // Filter the unit features by those who have level data
    const validUnitFeatures = unitFeatures.filter((unit) => {
      return !!validateLevelData(unit);
    });

    if (!validUnitFeatures || !validUnitFeatures.length) {
      this.onNoRoomsListed(task);
      return;
    }

    // Take first unit in the list
    const initialUnit = validUnitFeatures[0];
    const centroid = initialUnit.geometry && initialUnit.geometry.centroid;

    // Get the level and facility data to activate the level
    const view = Context.getInstance().views.activeView;
    const levelData = validateLevelData(initialUnit);
    const { facilityId } = levelData;
    const facilityData = levels.getFacilityData(facilityId);

    // Set the level and zoom to the unit
    Topic.publish(Topic.ActivateLevel, { facilityData, levelData, view });
    const feature = centroid ? { geometry: centroid } : initialUnit;
    goToFeature(view, feature, false, { scale: 300 });

    const when = criteria && criteria.when;
    const allDay = when.duration === "allDay";
    if (allDay && when.start.date) {
      const date = new Date(when.start.date);
      const start = dateUtil.toStartOfDay(date);
      const end = dateUtil.toEndOfDay(start);
      setReservationsDefExp(start, end);
    } else if (when.start.date && when.end.date) {
      const start = new Date(when.start.date);
      const end = new Date(when.end.date);
      setReservationsDefExp(start, end);
    }

    // Drop unit pins
    dropUnitPins(validUnitFeatures);
  }

  onNoRoomsListed(task) {
    const { criteria } = task;

    const when = criteria && criteria.when;
    const allDay = when.duration === "allDay";
    if (allDay && when.start.date) {
      const date = new Date(when.start.date);
      const start = dateUtil.toStartOfDay(date);
      const end = dateUtil.toEndOfDay(start);
      setReservationsDefExp(start, end);
    } else if (when.start.date && when.end.date) {
      const start = new Date(when.start.date);
      const end = new Date(when.end.date);
      setReservationsDefExp(start, end);
    }

    dropUnitPins([]);
  }

  getCriteria() {
    let criteria = Context.instance.session.meetingRoomsCriteria;
    if (!criteria) {
      let duration = "oneHour";
      let dtStart = dateUtil.getTopOfHour();
      let dtEnd = dateUtil.addDuration(dtStart,duration);
      criteria = Context.instance.session.meetingRoomsCriteria = {
        sortBy: "name",
        sortDir: "asc",
        when: {
          duration: duration,
          start: {
            date: dtStart.getTime()
          },
          end: {
            date: dtEnd.getTime()
          }
        },
        filter: {
          siteId: null,
          facilityId: null,
          levelId: null,
          capacity: null
        }
      };
      if (this.canSortByBluedotDistance() && this.isBluedotInSite()) {
        criteria.sortBy = "distanceBluedot";
      }
    }
    return criteria;
  }

  isBluedotInSite() {
    const graphic = Context.instance.bluedot.graphic;
    let levels = Context.instance.aiim.datasets.levels;
    if (graphic && graphic.geometry && levels && levels.hasSitesById()) {
      let site = levels.findSiteByPoint(graphic.geometry);
      if (site) return true;
    }
    return false;
  }

  makeCriteriaString(task) {
    const i18n = Context.instance.i18n;
    const djl = Context.instance.lib.dojo.locale;
    const when = task.criteria.when;
    const allDay = when.duration === "allDay";
    let msg = null;

    let dateFormat = {selector:"date", formatLength: "short" };
    let timeFormat = "H:mm";
    let locale = Context.instance.lib.dojo.kernel.locale
    if (locale === "en" || locale === "en-us") timeFormat = "h:mm A"

    if (when.start.date && when.end.date) {
      let dtNow = new Date();
      let dtStart = new Date(when.start.date);
      let dtEnd = new Date(when.end.date);
      let today = djl.format(dtNow,dateFormat);
      let start = djl.format(dtStart,dateFormat);
      let end = djl.format(dtEnd,dateFormat);
      let st = moment(dtStart).format(timeFormat);
      let et = moment(dtEnd).format(timeFormat);
      let isToday = (today === start) && (today === end);
      let isSameDay = (start === end);

      if (allDay) {
        let dur, day
        if (isToday) {
          dur = ""
          day = i18n.meetingRooms.today;
        } else if (isSameDay) {
          dur = i18n.meetingRooms.on;
          day = start;
        } else {
          dur = i18n.meetingRooms.from
          day = start + " - " + end
        }
        msg = i18n.meetingRooms.availableUnitsAllDay
        msg = msg.replace("{duration}", dur)
        msg = msg.replace("{day}", day)
      } else {
        let dur, dateString, pattern;
        if (isToday) {
          dateString = i18n.meetingRooms.today;
          dur = "";
          pattern = i18n.meetingRooms.availableUnitsSameDay;
        } else if (isSameDay) {
          dateString = start;
          dur = i18n.meetingRooms.on;
          pattern = i18n.meetingRooms.availableUnitsSameDay;
        } else {
          dur = i18n.meetingRooms.from;
          pattern = i18n.meetingRooms.availableUnits;
        }
        msg = pattern
          .replace("{duration}", dur)
          .replace("{date}", dateString)
          .replace("{startDate}", start)
          .replace("{startTime}", st)
          .replace("{endDate}", end)
          .replace("{endTime}", et);
      }
    }
    task.criteriaString = msg;
  }

  queryReservations(task) {
    const promise = new Promise((resolve,reject) => {
      let r = Context.instance.aiim.datasets.reservations;
      let qaopts = {};
      let when = this.getCriteria().when;
      let allDay = when.duration === "allDay";
      let start = when.start.date && dateUtil.getZulu(new Date(when.start.date));
      let end = when.end.date && dateUtil.getZulu(new Date(when.end.date));
      let w;
      if (allDay) {
        if (when.start.date) {
          let dt = new Date(when.start.date);
          let dtStart = dateUtil.toStartOfDay(dt);
          let dtEnd = dateUtil.toEndOfDay(dtStart);
          start = dateUtil.getZulu(dtStart);
          end = dateUtil.getZulu(dtEnd);
          w = "(("+r.startTimeField + " < TIMESTAMP '" + end+"')";
          w += " AND ("+r.endTimeField + " > TIMESTAMP '" + start+"'))";
          qaopts.where = w;
        }
      } else if (when.start.date && when.end.date) {
        w = "(("+r.startTimeField + " < TIMESTAMP '" + end+"')";
        w += " AND ("+r.endTimeField + " > TIMESTAMP '" + start+"'))";
        qaopts.where = w;
      }

      if (qaopts.where) {
        w = "("+r.stateField+" IN (0,1,4))"; // pending, approved, checked-in
        qaopts.where = "("+qaopts.where+" AND "+w+")";
      }

      //qaopts.where = "1=1";
      //console.log("reservations.where",qaopts.where)

      Promise.resolve().then(() => {
        if (!qaopts.where) {
          task.reservedUnits = null;
        } else {
          return r.queryUnitIds(qaopts).then(info => {
            task.reservedUnits = info;
            //console.log("reservedUnits",info)
          });
        }
      }).then(result => {
        return this.filterUnits(task)
      }).then(result => {
        this.makeCriteriaString(task);
        if (this.parentComponent && this.parentComponent._mounted) {
          Context.instance.session.sharedMeetingRoomWhen = {
            duration: when.duration,
            start: {
              date: when.start.date
            },
            end: {
              date: when.end.date
            }
          };
        }
        resolve();
      }).catch(ex => {
        reject(ex);
      });
    });
    return promise;
  }

  queryUnits() {
    const promise = new Promise((resolve,reject) => {
      const items = [];
      const ds = Context.instance.aiim.datasets.units;
      const lds = Context.instance.aiim.datasets.levels;
      if (!ds || !ds.layer2D) return;

      let fld = aiimUtil.findField(ds.layer2D.fields,FieldNames.RESERVATION_METHOD);
      let oidFld = ds.layer2D.objectIdField;
      let uidFld = aiimUtil.findField(ds.layer2D.fields,FieldNames.UNIT_ID);
      let aidFld = aiimUtil.findField(ds.layer2D.fields,FieldNames.AREA_ID);
      let lidFld = Context.instance.aiim.getLevelIdField(ds.layer2D);
      let nameFld = aiimUtil.findField(ds.layer2D.fields,FieldNames.NAME);
      let capFld = aiimUtil.findField(ds.layer2D.fields,FieldNames.UNITS_CAPACITY);
      if (!fld) return;

      const task = {
        criteria: this.getCriteria(),
        criteriaString: null,
        items: null,
        filteredItems: null,
        reservedUnits: null
      }

      let f = fld.name;
      let where = "(("+f+" = 1) OR ("+f+" = 2))";
      where = "("+where+" AND ("+aidFld.name+" IS NULL))";
      //let n = 0;
      const qaopts = {
        where: where,
        perFeatureCallback: feature => {
          let item = {
            oid: feature.attributes[oidFld],
            unitId: uidFld && feature.attributes[uidFld.name],
            levelId: lidFld && feature.attributes[lidFld.name],
            name: nameFld && feature.attributes[nameFld.name],
            capacity: capFld && feature.attributes[capFld.name],
            reservationMethod: feature.attributes[f],
            sfl: ds.getSiteFacilityLevelStrings(ds.source,feature),
            feature: feature
          };

          // n++;
          // item.capacity = 7;
          // if (n === 2) item.capacity = 8;
          // if (n === 3) item.capacity = 6;

          item.hasCapacity = (typeof item.capacity === "number");
          let ld = lds.getLevelData(item.levelId)
          item.facilityId = ld && ld.facilityId;
          item.siteId = ld && ld.siteId;
          items.push(item);
        }
      };

      ds.queryAll(qaopts).then(() => {
        task.items = items;
        //return this.filterUnits(task);
        return this.queryReservations(task);
      }).then(() => {
        if (!this.parentComponent._mounted) return;
        this.parentComponent.setState({
          criteriaString: task.criteriaString,
          items: task.items,
          filteredItems: task.filteredItems,
          reservedUnits: task.reservedUnits,
          working: false,
          wasSiteInit: true
        });

        // Drop pins, filter layer, and zoom to first feature
        this.onRoomsListed(task);
      }).catch(ex => {
        console.error(ex);
        if (ex && isNetworkError(ex.message)) {
          const i18n = Context.getInstance().i18n;
          Topic.publishNetworkError(i18n.meetingRooms.issues.m3);
        } else {
          Topic.publishErrorAccessingData();
        }
        if (!this.parentComponent._mounted) return;
        this.parentComponent.setState({working: false, wasSiteInit: true});
      });
    });
    return promise;
  }

  async sortByDistance(task) {
    const filteredItems = task.filteredItems;
    const featureItems = filteredItems.map(item => {
      return {
        feature: item.feature,
        filteredItem: item
      };
    });
    const cf = new ClosestFacility();
    const ds = Context.instance.aiim.datasets.units;
    const source = ds.getSource();
    const useBluedot = task.criteria.sortBy === "distanceBluedot";
    let fromFeature, fromSource, voField;

    if (useBluedot) {
      let graphic = Context.instance.bluedot.graphic;
      if (graphic) {
        fromFeature = graphic;
      }
    } else {
      fromFeature = cf.getFromFeature();
      fromSource = cf.getFromSource();
    }

    if (fromFeature && fromFeature.geometry) {
      cf.euclideanSort(fromFeature, featureItems, voField, fromSource, source);
      //console.log("euclideanSort.featureItems",featureItems);
      const max = cf.proximityMaxItemsToSort;
      const topItems = featureItems.slice(0,max);
      if (topItems.length > 0) {
        try {
          await cf.networkSort(fromFeature,topItems,true);
          for (let i=0;i<topItems.length;i++) {
            featureItems[i] = topItems[i];
          }
          task.filteredItems = featureItems.map(item => item.filteredItem);
        } catch (ex) {
          console.error("Error sorting by distance",ex);
          task.filteredItems = featureItems.map(item => item.filteredItem);
        }
      }
    }
  }

  sortUnits(task) {
    const promise = new Promise(async (resolve,reject) => {
      const criteria = task.criteria;
      const filteredItems = task.filteredItems;
      let sortBy = criteria.sortBy || "name";
      let sortDir = criteria.sortDir || "asc";
      let byName = (sortBy === "name");
      let byCapacity = (sortBy === "capacity");
      let byDistance = (sortBy === "distanceHome") || (sortBy === "distanceBluedot");

      if (filteredItems && filteredItems.length > 0) {
        if (byName || byCapacity) {
          filteredItems.sort((a,b) => {
            if (byName) {
              if (a.name && b.name) {
                return a.name.localeCompare(b.name);
              } else if(a.name) {
                return -1;
              } else if(b.name) {
                return 1;
              }
            } else {
              if (a.hasCapacity && b.hasCapacity) {
                return a.capacity - b.capacity;
              } else if (a.hasCapacity) {
                return -1;
              } else if (b.hasCapacity) {
                return 1;
              }
            }
            return 0;
          });
          if (sortDir === "desc") {
            filteredItems.reverse();
          }
          task.filteredItems = filteredItems;
        } else if (byDistance) {
          await this.sortByDistance(task);
        }
      }
      resolve();
    });
    return promise;
  }

}
